import React, {
    useRef,
    useState,
    useEffect,
    useCallback,
    Fragment,
} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Icon } from '@gx-design/icon';
import {
    GlobalHandler,
    Loader,
} from '../../components';
import arrayMove from '../../../lib/array-move';
import { SortableContainer, SortableElement } from 'react-sortable-hoc/dist/react-sortable-hoc.js';
import useClickOutside from '../../hooks/useClickOutside';
import { Button } from '@gx-design/button';

function stopPrevent(fn) {
    return e => {
        e.preventDefault();
        e.stopPropagation();
        return fn(e);
    };
}

const Dropzone = ({ children, className, disabled, onDrop, styles }) => {
    //dragging => drag and drop
    const [dragging, setDragging] = useState(0);
    const containerRef = useRef();

    const handleDragEnter = e => {
        e.preventDefault();
        setDragging(dragging + 1);
    };

    const handleDragLeave = e => {
        e.preventDefault();
        setDragging(dragging - 1);
    };

    const handleDragOver = stopPrevent(e => {
        e.dataTransfer.dropEffect = 'copy';
    });

    const handleDrop = stopPrevent(e => {
        const droppedFiles = Array.from(e.dataTransfer.files);
        const files = droppedFiles.slice(0, 1);
        setDragging(0);
        !disabled && onDrop(files);
    });

    return (
        <div
            ref={containerRef}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            className={classNames([
                styles.dropzone,
                {
                    [styles.disabledDropzone]: disabled,
                    [styles.dropzoneActive]: dragging,
                },
                className,
            ])}
        >
            {children}
        </div>
    );
};

Dropzone.propTypes = {
    disabled: PropTypes.bool,
    multiple: PropTypes.bool,
    onDrop: PropTypes.func,
    styles: PropTypes.object,
};

Dropzone.defaultProps = {
    disabled: false,
    multiple: true,
    onDrop: () => {},
};

const SortableFilePreview = SortableElement(
    ({
        children,
        className,
        disabled,
        isSelected,
        isMobile,
        isRemovable,
        isHighlighted,
        onFileClick,
        onFileHover,
        onFileOut,
        onRemove,
        title,
        style,
    }) => {
        const fileRef = useRef();
        useClickOutside(fileRef, () => handleGlobalClick());

        const handleGlobalClick = evt => {
            if (
                !fileRef ||
                !fileRef.current ||
                !fileRef.current.classList.contains(style.fileSelected)
            ) {
                return;
            }

            onFileOut();
        };

        return (
            <div
                className={classNames([
                    style.media,
                    className,
                    {
                        [style.fileDisabled]: disabled,
                        [style.fileSelected]: isSelected || isHighlighted,
                    },
                ])}
                onClick={!isHighlighted && !disabled ? onFileClick : null}
                onMouseEnter={!isMobile ? onFileHover : null}
                onMouseLeave={!isMobile ? onFileOut : null}
                ref={fileRef}
                title={isSelected && !isHighlighted ? title : null}
            >
                {children}
                <Fragment>
                    {isRemovable && isSelected && !disabled ? (
                        <span
                            className="img-delete"
                            onClick={onRemove}
                            title="Cancella l'immagine"
                        >
                            <Icon name="cross-circle--active" className="gx-icon--negative" />
                        </span>
                    ) : null}
                </Fragment>
            </div>
        );
    }
);

const FilePreview = ({
    children,
    disabled,
    onRemove,
    isMobile,
    isSelected,
    isRemovable,
    onFileOut,
    onFileClick,
    onFileHover,
    style,
}) => {
    const fileRef = useRef();

    const handleGlobalClick = evt => {
        if (
            !fileRef ||
            !fileRef.current ||
            !fileRef.current.classList.contains(style.fileSelected)
        ) {
            return;
        }

        if (!fileRef.current.contains(evt.target)) {
            onFileOut();
        }
    };

    return (
        <GlobalHandler event="click" on="document" callback={handleGlobalClick}>
            <div
                className={classNames([
                    style.media,
                    {
                        [style.fileSelected]: isSelected,
                        [style.fileDisabled]: disabled,
                    },
                ])}
                onClick={isMobile || !isRemovable ? onFileClick : null}
                onMouseEnter={!isMobile ? onFileHover : null}
                onMouseLeave={!isMobile ? onFileHover : null}
                ref={fileRef}
            >
                {children}
                <Fragment>
                    {isRemovable && isSelected && !disabled ? (
                        <span
                            className="img-delete"
                            onClick={onRemove}
                            title="Cancella l'immagine"
                        >
                            <Icon name="cross-circle" className="gx-icon--negative" />
                    </span>
                    ) : null}
                </Fragment>
            </div>
        </GlobalHandler>
    );
};

const FileList = SortableContainer(({ children, styles }) => (
    <div className={styles.imagesBox}>{children}</div>
));

const enqueue = files => prevState => [...prevState, ...files];

const dequeue = file => prevState => prevState.filter(f => f !== file);

export const GalleryUploader = ({
    accept,
    className,
    files,
    inputId,
    isEditing,
    isMobile,
    isRemovable,
    loading,
    maxFiles,
    maxFileSize,
    highlighted,
    mimeRegexp,
    multiple,
    navigate,
    onFileAdd,
    onFileSort,
    onFileRemove,
    onFileClick,
    onFileSizeExceed,
    onMaxFiles,
    fileTitle,
    renderPreview,
    styles,
    ...other
}) => {
    const [uploadQueue, setUploadQueue] = useState([]);
    const [fileSelected, setFileSelected] = useState(null);
    const [scrollRight, setScrollRight] = useState(false);
    const [scrollLeft, setScrollLeft] = useState(false);
    const [thumbLoading, setThumbLoading] = useState(null);
    const inputRef = useRef();
    const filesListRef = useRef(null);
    const navigateRightButtonRef = useRef();
    const navigateLeftButtonRef = useRef();
    const offset = 50;

    const fileIsValid = file => {
        if (
            (mimeRegexp && !file.type.match(mimeRegexp)) ||
            (maxFileSize && file.size > maxFileSize)
        ) {
            if (onFileSizeExceed) {
                setTimeout(onFileSizeExceed, 1000);
            }

            return false;
        }

        return true;
    };

    const processQueue = queue => {
        if (!queue.length) {
            return;
        }

        const file = queue.shift();
        onFileAdd(file, () => {
            setUploadQueue(dequeue(file));
            processQueue(queue);
            handleNavigationButtons();
        });
    };

    const handleFileAdd = newFiles => {
        setFileSelected(null);

        if (onFileAdd) {
            newFiles = newFiles
                .slice(0, maxFiles - files.length)
                .filter(file => fileIsValid(file));
            setUploadQueue(enqueue(newFiles));
        }

        inputRef.current.value = '';
    };

    const handleFileSort = ({ oldIndex, newIndex }) => {
        if (oldIndex !== newIndex && onFileSort) {
            onFileSort(
                arrayMove(files, oldIndex, newIndex),
                oldIndex,
                newIndex
            );
        }
    };

    const handleFileHover = fileIndex => {
        let nextSelected = fileIndex !== fileSelected ? fileIndex : null;
        setFileSelected(nextSelected);
    };

    const handleFileClick = fileIndex => {
        let nextSelected = fileIndex !== fileSelected ? fileIndex : null;
        setFileSelected(nextSelected);
        onFileClick && onFileClick(fileIndex);
    };

    const handleRemoveSelect = () => {
        setFileSelected(null);
    };

    const handleFileRemove = (file, position) => {
        setThumbLoading(position);

        onFileRemove(
            file,
            () => {
                setFileSelected(null);
                setThumbLoading(null);
            },
            () => {
                setThumbLoading(null);
            }
        );
    };

    const renderQueue = () => {
        return uploadQueue.map((file, index) => (
            <div key={index} className={styles.media}>
                <Loader
                    loading={true}
                    fixedOverlay={false}
                    inlineOverlay={true}
                    centered={false}
                />
            </div>
        ));
    };

    const renderFiles = () => {
        return files.map((file, index) => (
            <SortableFilePreview
                key={`item-${file.id ? file.id : window.btoa(file.thumb)}`}
                index={index}
                disabled={thumbLoading !== null}
                isMobile={isMobile}
                title={fileTitle}
                isSelected={index === fileSelected}
                isHighlighted={index === highlighted}
                isRemovable={isRemovable(file)}
                onFileHover={() => handleFileHover(index)}
                onFileClick={() => handleFileClick(index)}
                onRemove={stopPrevent(() => handleFileRemove(file, index))}
                onFileOut={() => handleRemoveSelect()}
                style={styles}
            >
                <Loader
                    loading={thumbLoading !== null && thumbLoading === index}
                    fixedOverlay={false}
                    inlineOverlay={true}
                />
                {renderPreview(file)}
            </SortableFilePreview>
        ));
    };

    const getTranslateX = () => {
        const transformStyle = filesListRef.current.style.transform;
        const translateX = transformStyle.replace(/[^\-+\d.]/g, '');

        return translateX || 0;
    };

    const setButtonsScroll = (filesList, btnLeft, btnRight) => {
        setScrollLeft(btnLeft.x + btnLeft.width > filesList.x);
        setScrollRight(btnRight.x < filesList.x + filesList.width);
    };

    const handleNavigationButtons = () => {
        if (filesListRef && filesListRef.current) {
            const filesListRect = filesListRef.current.getBoundingClientRect();
            const navigateLeftButtonRect = navigateLeftButtonRef.current.getBoundingClientRect();
            const navigateRightButtonRect = navigateRightButtonRef.current.getBoundingClientRect();

            setButtonsScroll(
                filesListRect,
                navigateLeftButtonRect,
                navigateRightButtonRect
            );
        }
    };

    const handleNavigation = direction => {
        const translateX = parseInt(getTranslateX());
        const nextTranslateX =
            direction === 'left' ? translateX + offset : translateX - offset;
        filesListRef.current.style.transform = nextTranslateX
            ? `translateX(${nextTranslateX}px)`
            : null;
        handleNavigationButtons();
    };

    const setFilesListRef = useCallback(node => {
        if (node) {
            const filesListRect = node.getBoundingClientRect();
            const navigateLeftButtonRect = navigateLeftButtonRef.current.getBoundingClientRect();
            const navigateRightButtonRect = navigateRightButtonRef.current.getBoundingClientRect();

            setButtonsScroll(
                filesListRect,
                navigateLeftButtonRect,
                navigateRightButtonRect
            );
        }

        return (filesListRef.current = node);
    }, []);

    useEffect(() => {
        processQueue(uploadQueue);
    }, [uploadQueue]);

    useEffect(() => {
        window.addEventListener('resize', handleNavigationButtons);

        return () =>
            window.removeEventListener('resize', handleNavigationButtons);
    }, []);

    return (
        <Fragment>
            {navigate ? (
                <Button
                    className={classNames([
                        styles.filesNavigate,
                        styles.navigatePrev,
                    ])}
                    iconOnly
                    onClick={() => handleNavigation('left')}
                    ref={navigateLeftButtonRef}
                    disabled={!scrollLeft}
                >
                    <Icon name="arrow-left" />
                </Button>
            ) : null}
            <Dropzone
                className={className}
                disabled={files.length >= maxFiles}
                onDrop={handleFileAdd}
                styles={styles}
            >
                {loading ? (
                    <Loader
                        loading={true}
                        fixedOverlay={false}
                        inlineOverlay={true}
                        centered={false}
                    />
                ) : (
                    <Fragment>
                        <input
                            type="file"
                            id={inputId}
                            multiple={!!multiple}
                            className={styles.fileInput}
                            disabled={thumbLoading !== null}
                            accept={accept}
                            onChange={e =>
                                handleFileAdd(Array.from(e.target.files))
                            }
                            ref={inputRef}
                        />
                        <FileList
                            disabled={!isEditing}
                            isMobile={isMobile}
                            axis="xy"
                            onSortEnd={handleFileSort}
                            helperClass={styles.moveImg}
                            styles={styles}
                            {...other}
                        >
                            <div
                                ref={setFilesListRef}
                                className={
                                    files.length >= maxFiles - 1 &&
                                    styles.filesContainerLarge
                                        ? styles.filesContainerLarge
                                        : styles.filesContainer
                                }
                            >
                                <div
                                    className={classNames([
                                        styles.media,
                                        styles.addItem,
                                    ])}
                                    onClick={() =>
                                        files.length >= maxFiles
                                            ? onMaxFiles()
                                            : inputRef.current.click()
                                    }
                                >
                                    <Icon name="plus" />
                                </div>
                                {renderQueue()}
                                {renderFiles()}
                            </div>
                        </FileList>
                    </Fragment>
                )}
            </Dropzone>
            {navigate ? (
                <Button
                    className={classNames([
                        styles.filesNavigate,
                        styles.navigateNext,
                    ])}
                    iconOnly
                    onClick={() => handleNavigation('right')}
                    ref={navigateRightButtonRef}
                    disabled={!scrollRight}
                >
                    <Icon name="arrow-right" />
                </Button>
            ) : null}
        </Fragment>
    );
};

GalleryUploader.propTypes = {
    /** Regular expression per i mime/type accettati. */
    accept: PropTypes.string,
    /** Regular expression per i mime/type accettati. */
    mimeRegexp: PropTypes.string,
    /** Dimensione massima del file */
    maxFileSize: PropTypes.number,
    /** Lista di file */
    files: PropTypes.array,
    /** Funzione di rendering dell'anteprima */
    renderPreview: PropTypes.func,
    /** File evidenziato */
    highlighted: PropTypes.number,
    /** Numero massimo di file caricabili */
    maxFiles: PropTypes.number,
    /** Caricamento multiplo */
    multiple: PropTypes.bool,
    /** Id per legare la label/placeholder al suo input file per il caricamento */
    inputId: PropTypes.string,
    /** Pulsanti di navigazione laterali */
    navigate: PropTypes.bool,
    /** Stato di caricamento */
    loading: PropTypes.bool,
    /** Stile componente */
    styles: PropTypes.object,
    /** title del file  */
    fileTitle: PropTypes.string,

    /** Evento scatenato quando si aggiunge un file */
    onFileAdd: PropTypes.func,
    /** Evento scatenato quando si rimuove un file */
    onFileRemove: PropTypes.func,
    /** Evento scatenato quando si riordinano i file */
    onFileSort: PropTypes.func,
    /** Evento scatenato quando si clicca i file */
    onFileClick: PropTypes.func,
    /** Evento scatenato quando i file superano la dimensione massima */
    onFileSizeExceed: PropTypes.func,
    /** Evento scatenato si prova a caricare un file a limite raggiunto */
    onMaxFiles: PropTypes.func,
};

GalleryUploader.defaultProps = {
    renderPreview: image => <img src={image} />,
    maxFiles: 10,
    multiple: true,
    pressDelay: 200,
    highlighted: 0,
    useWindowAsScrollContainer: true,
    fileTitle: 'Trascina per cambiare posizione',
    inputId: 'uploadfile',
    navigate: false,
    loading: false,
};

export const Uploader = ({
    accept,
    className,
    counter,
    files,
    browseInputId,
    isMobile,
    label,
    loading,
    maxFiles,
    maxFileSize,
    mimeRegexp,
    multiple,
    onFileAdd,
    onFileRemove,
    onFileSizeExceed,
    renderPreview,
    styles,
    uploadRequirementsText,
}) => {
    const [uploadQueue, setUploadQueue] = useState([]);
    const [fileSelected, setFileSelected] = useState(null);
    const [loadingThumbs, setLoadingThumbs] = useState([]);
    const browseInput = useRef();

    const fileIsValid = file => {
        if (
            (mimeRegexp && !file.type.match(mimeRegexp)) ||
            (maxFileSize && file.size > maxFileSize)
        ) {
            if (onFileSizeExceed) {
                setTimeout(onFileSizeExceed, 1000);
            }

            return false;
        }

        return true;
    };

    const addLoadingThumb = thumb => {
        const nextLoadingThumbs = loadingThumbs.concat(thumb);
        setLoadingThumbs(nextLoadingThumbs);
    };

    const removeLoadingThumb = thumb => {
        const nextThumbLoading = loadingThumbs.filter(item => item !== thumb);
        setLoadingThumbs(nextThumbLoading);
    };

    const processQueue = queue => {
        if (!queue.length) {
            return;
        }

        const file = queue.shift();
        onFileAdd(
            file,
            () => {
                setUploadQueue(dequeue(file));
                processQueue(queue);
                removeLoadingThumb(files.length);
            },
            () => {
                removeLoadingThumb(files.length);
            }
        );
    };

    const handleFileAdd = newFiles => {
        setFileSelected(null);

        if (onFileAdd) {
            newFiles = newFiles
                .slice(0, maxFiles - files.length)
                .filter(file => fileIsValid(file));

            if (!newFiles || !newFiles.length) {
                browseInput.current.value = '';
                return;
            }

            let i =
                loadingThumbs.length > 0
                    ? Math.max(...loadingThumbs) + 1
                    : files.length;
            addLoadingThumb(i);
            setUploadQueue(enqueue(newFiles));
        }

        browseInput.current.value = '';
    };

    const handleFileSelect = fileIndex => {
        let nextSelected = fileIndex !== fileSelected ? fileIndex : null;
        setFileSelected(nextSelected);
    };

    const handleRemoveSelect = () => {
        setFileSelected(null);
    };

    const handleFileRemove = (file, filePosition) => {
        addLoadingThumb(filePosition);

        onFileRemove(
            file,
            () => {
                setFileSelected(null);
                removeLoadingThumb(filePosition);
            },
            () => {
                removeLoadingThumb(filePosition);
            }
        );
    };

    const isDropzoneDisabled = () => {
        return (
            files.length >= maxFiles || (loadingThumbs.length > 0 && !multiple)
        );
    };

    useEffect(() => {
        processQueue(uploadQueue);
    }, [uploadQueue]);

    const renderFiles = () => {
        let thumbs = [];

        for (let index = 0; index < maxFiles; index++) {
            thumbs.push(
                <div key={`container-${index}`} className={styles.thumb}>
                    <div key={`sub-container-${index}`}>
                        <FilePreview
                            index={index}
                            style={styles}
                            isMobile={isMobile}
                            isSelected={index === fileSelected}
                            onRemove={stopPrevent(() =>
                                handleFileRemove(files[index], index)
                            )}
                            onFileClick={
                                files[index] && files[index].id
                                    ? () => handleFileSelect(index)
                                    : e => (
                                          e.preventDefault(),
                                          browseInput.current.click()
                                      )
                            }
                            isRemovable={files[index] && files[index].id}
                            onFileHover={() => handleFileSelect(index)}
                            onFileOut={handleRemoveSelect}
                            disabled={loadingThumbs.length > 0}
                        >
                            <Loader
                                loading={
                                    loadingThumbs.length > 0 &&
                                    loadingThumbs.indexOf(index) > -1
                                }
                                fixedOverlay={false}
                                centered={false}
                            />
                            {files[index] && files[index].id ? (
                                renderPreview(files[index])
                            ) : (
                                <Icon name="plus" />
                            )}
                        </FilePreview>
                    </div>
                </div>
            );
        }

        return thumbs;
    };

    return (
        <Fragment>
            <div className={styles.head}>
                {label ? <label>{label}</label> : null}
                {counter ? (
                    <div
                        className={styles.counter}
                    >{`${files.length}/${maxFiles}`}</div>
                ) : null}
            </div>
            <Dropzone
                className={className}
                disabled={isDropzoneDisabled()}
                multiple={!!multiple}
                onDrop={handleFileAdd}
                styles={styles}
            >
                {loading ? (
                    <Loader
                        loading={true}
                        fixedOverlay={false}
                        inlineOverlay={false}
                        centered={false}
                    />
                ) : (
                    <Fragment>
                        <div className={styles.uploadText}>
                            {!isMobile
                                ? "Trascina i file in quest'area oppure"
                                : null}{' '}
                            <a
                                href="#"
                                onClick={e => (
                                    e.preventDefault(),
                                    !loading && !isDropzoneDisabled()
                                        ? browseInput.current.click()
                                        : null
                                )}
                            >
                                sfoglia
                            </a>{' '}
                            per caricare.
                        </div>
                        <span className={styles.uploadRequirementsText}>
                            {uploadRequirementsText}
                        </span>
                        <input
                            type="file"
                            id={browseInputId}
                            multiple={!!multiple}
                            disabled={loading || isDropzoneDisabled()}
                            className={styles.browseInput}
                            accept={accept}
                            onChange={e =>
                                handleFileAdd(Array.from(e.target.files))
                            }
                            ref={browseInput}
                        />
                    </Fragment>
                )}
            </Dropzone>
            <div className={styles.thumbsList}>{renderFiles()}</div>
        </Fragment>
    );
};

Uploader.propTypes = {
    /** Regular expression per i mime/type accettati. */
    accept: PropTypes.string,
    /** Regular expression per i mime/type accettati. */
    mimeRegexp: PropTypes.string,
    /** Dimensione massima del file */
    maxFileSize: PropTypes.number,
    /** Lista di file */
    files: PropTypes.array,
    /** Funzione di rendering dell'anteprima */
    renderPreview: PropTypes.func,
    /** Numero massimo di file caricabili */
    maxFiles: PropTypes.number,
    /** Caricamento multiplo */
    multiple: PropTypes.bool,
    /** Id per legare il testo "sfoglia file" al suo input file per il caricamento */
    browseInputId: PropTypes.string,
    /** Stato di caricamento */
    loading: PropTypes.bool,
    /** Visibilità contatore */
    counter: PropTypes.bool,
    /** Visibilità label */
    label: PropTypes.string,
    /** Stile componente */
    styles: PropTypes.object,
    /** Device mobile */
    isMobile: PropTypes.bool,

    /** Evento scatenato quando si aggiunge un file */
    onFileAdd: PropTypes.func,
    /** Evento scatenato quando si rimuove un file */
    onFileRemove: PropTypes.func,
    /** Evento scatenato quando si riordinano i file */
    onFileSort: PropTypes.func,
    /** Evento scatenato quando i file superano la dimensione massima */
    onFileSizeExceed: PropTypes.func,
};

Uploader.defaultProps = {
    counter: true,
    label: true,
    isMobile: false,
    renderPreview: image => <img src={image} />,
    maxFiles: 4,
    multiple: true,
    pressDelay: 200,
    useWindowAsScrollContainer: true,
    browseInputId: 'browsefile',
    navigate: false,
    loading: false,
};
